import { useRef, useState, type FormEvent } from "react";
import { CONTACT_EMAIL, sendContact, type ContactValues } from "../lib/contact";

export type ContactStatus = "idle" | "sending" | "success" | "error";

const EMPTY: ContactValues = { name: "", email: "", message: "", website: "" };

// Plain bordered form. `initialStatus`/`initialError` exist so tests can render
// each state without a DOM; `fetchImpl` lets tests mock the network.
export function ContactForm({
  initialStatus = "idle",
  initialError = "",
  fetchImpl,
}: {
  initialStatus?: ContactStatus;
  initialError?: string;
  fetchImpl?: typeof fetch;
}) {
  const [values, setValues] = useState<ContactValues>(EMPTY);
  const [status, setStatus] = useState<ContactStatus>(initialStatus);
  const [error, setError] = useState(initialError);
  const inFlight = useRef(false);

  const set = (k: keyof ContactValues) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    setStatus("sending");
    setError("");
    const result = await sendContact(values, fetchImpl);
    inFlight.current = false;
    if (result.ok) {
      setValues(EMPTY);
      setStatus("success");
    } else {
      setError(result.error);
      setStatus("error");
    }
  }

  const sending = status === "sending";

  return (
    <form className="cform" onSubmit={onSubmit} aria-busy={sending}>
      <div className="cform-field">
        <label htmlFor="cf-name">Name</label>
        <input
          id="cf-name"
          name="name"
          type="text"
          autoComplete="name"
          required
          maxLength={100}
          value={values.name}
          onChange={set("name")}
        />
      </div>
      <div className="cform-field">
        <label htmlFor="cf-email">Email</label>
        <input
          id="cf-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          maxLength={254}
          value={values.email}
          onChange={set("email")}
        />
      </div>
      <div className="cform-field">
        <label htmlFor="cf-message">Message</label>
        <textarea
          id="cf-message"
          name="message"
          rows={7}
          required
          minLength={10}
          maxLength={5000}
          value={values.message}
          onChange={set("message")}
        />
      </div>
      <div className="cform-hp" aria-hidden="true">
        <label htmlFor="cf-website">Website</label>
        <input
          id="cf-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={set("website")}
        />
      </div>

      <button type="submit" className="cta primary cform-submit" disabled={sending}>
        {sending ? "sending…" : "send message"}
      </button>

      <div className="cform-status" aria-live="polite">
        {status === "success" && (
          <p className="cform-ok">
            <b>sent</b> — thanks, your message is on its way. I will reply by email.
          </p>
        )}
      </div>
      {status === "error" && (
        <p className="cform-err" role="alert">
          <b>error</b> — {error}
        </p>
      )}
      <p className="cform-fallback">
        {status === "error" ? "Sending failed? " : "Prefer email? "}
        Write to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </form>
  );
}
